import React from 'react';
import Reveal from './Reveal';

// ponytail: native <details>/<summary> accordion — no JS state, keyboard/a11y for free.
const FAQS = [
  {
    q: 'What does Emboscale actually do?',
    a: 'You describe a production line in plain language. The Factory Agent plans the factory floor — a dimensioned 3D layout — balances the line, builds shift schedules, simulates throughput, writes digital work instructions, and programs the robot cells. Every program passes an 8-check kinematic verification before you see it marked verified.',
  },
  {
    q: 'Can AI really write robot programs?',
    a: "Yes — with a catch that matters. The agent authors pick-and-place, palletizing, screwdriving, and welding programs on industry-standard 6-axis robot models with datasheet kinematics. But AI can get it wrong, which is why every program must pass joint-limit, continuity, timing, and collision checks (600+ samples per program) before it counts. Anything out of reach or over payload gets refused with the number.",
  },
  {
    q: 'Can Emboscale generate a factory layout from a description?',
    a: 'Yes — that is the starting point. Describe the line in chat and the factory layout comes back dimensioned: stations, machines, conveyors, walls, safety zones, as a full 3D digital twin. If you have CAD, upload STEP assemblies and the agent extracts the BOM and places real part meshes into the station scenes. DXF floor plans and Excel data work too.',
  },
  {
    q: 'Is this a simulation tool or a planning tool?',
    a: "Both, in one loop. Shift schedules come from a deterministic CP-SAT solver — a real Gantt with due-date-risk flagging, not LLM guesses. Line balancing rolls cycle times up across stations, and throughput runs through discrete-event simulation: takt, utilization, and bottlenecks per station. A readiness gate tells you exactly what still blocks production, and why.",
  },
  {
    q: 'Is AI-generated robot code safe to run on my line?',
    a: "The code runs unmodified on the real robot controller software, and verification catches mistakes before they reach you — but your engineers stay in charge. An approvals workflow gates consequential changes, every edit records who made it (agent or human), and your PLC and safety engineers keep the final word.",
  },
  {
    q: 'How is this different from traditional offline robot programming (OLP)?',
    a: 'Traditional OLP tools make you do the programming in a different interface. Here you prompt the task, the agent writes and verifies the program, compares multiple strategies, and optimizes cycle time — 27% faster on our benchmark cell. Planning, simulation, instructions, and robot programming share one context instead of living in separate tools.',
  },
  {
    q: 'How long does it take to get a factory plan?',
    a: 'About 30 minutes from prompt to first draft: layout, line balance, shift schedule, work instructions, and robot programs, with verification already run. From there you iterate by prompting — change the takt, swap a station, add a shift — instead of restarting a weeks-long planning cycle.',
  },
  {
    q: 'Do we have to replace our ERP or MES?',
    a: 'No. Emboscale layers on top of what you run today. It takes what you already have — DXF floor plans, STEP assemblies, Excel data — and hands back files your existing systems recognize: vendor-native robot code, STEP, DXF, XLSX, and AutomationML. Nothing gets ripped out.',
  },
  {
    q: 'What do I get out at the end?',
    a: 'Files your floor already recognizes: vendor-native robot code, deploy packs with safety-zone proposals and full verification provenance, STEP bundles, DXF plans, XLSX factory docs, and AutomationML.',
  },
];

const FAQSection = () => (
  <section id="faq" className="py-28 bg-white dark:bg-black border-t border-black/10 dark:border-white/10">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <div className="font-mono text-xs tracking-[0.25em] uppercase text-emerald-600 dark:text-emerald-400 mb-4">06 — Questions</div>
        <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 dark:text-white mb-12">
          Fair questions
        </h2>
      </Reveal>
      <Reveal>
        <div className="divide-y divide-black/10 dark:divide-white/10 border-y border-black/10 dark:border-white/10">
          {FAQS.map((f) => (
            <details key={f.q} className="group py-5">
              <summary className="flex items-center justify-between cursor-pointer list-none text-lg font-semibold text-gray-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors duration-200 [&::-webkit-details-marker]:hidden">
                {f.q}
                <span className="ml-4 text-emerald-600 dark:text-emerald-400 transition-transform duration-200 group-open:rotate-45 text-2xl leading-none flex-shrink-0">+</span>
              </summary>
              <p className="mt-4 text-gray-600 dark:text-gray-400 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </Reveal>
    </div>
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }),
      }}
    />
  </section>
);

export default FAQSection;
